// Menu items management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import type { MenuItem } from '@/types/entities'
import type { MenuItemWithIngredients, MenuItemIngredientInput } from '@/types/api.types'
import { applyLanguageFallback } from '@/lib/language-fallback'
import { isAssistantLoggedIn } from '@/lib/permissions'

/**
 * Fetch all menu items for a specific restaurant
 * Uses RPC function to bypass RLS for staff access
 */
export function useMenuItems(restaurantId: string | null) {
  return useQuery({
    queryKey: queryKeys.menuItems.list({ restaurantId }),
    staleTime: 5 * 60 * 1000, // 5 minutes - menu items don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Menu items don't need real-time updates
    refetchOnMount: false, // Don't refetch if data is fresh
    queryFn: async (): Promise<MenuItem[]> => {
      if (!restaurantId) {
        return []
      }

      // Use RPC function to bypass RLS (allows staff to view menu items)
      const { data, error } = await supabase
        .rpc('get_restaurant_menu_items', { p_restaurant_id: restaurantId })

      if (error) {
        throw error
      }

      return (data || []).map((item: any) => ({
        id: item.id,
        restaurantId: item.restaurant_id,
        nameAr: item.name_ar,
        nameFr: item.name_fr,
        descriptionAr: item.description_ar || null,
        descriptionFr: item.description_fr || null,
        price: item.price,
        imageUrl: item.image_url || null,
        imagePath: item.image_path || null,
        isAvailable: item.is_available ?? true,
        categoryId: item.category_id || null,
        optionsAreRequired: item.options_are_required ?? false,
        createdAt: item.created_at || '',
        updatedAt: item.updated_at || '',
      }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch single menu item with ingredients
 */
export function useMenuItemWithIngredients(menuItemId: string) {
  return useQuery({
    queryKey: queryKeys.menuItems.withIngredients(menuItemId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<MenuItemWithIngredients> => {
      const { data, error } = await supabase
        .from('menu_items')
        .select(`
          *,
          menu_item_ingredients (
            id,
            ingredient_id,
            quantity,
            unit,
            menu_item_option_id,
            created_at,
            ingredients (
              id,
              restaurant_id,
              name_ar,
              name_fr
            )
          )
        `)
        .eq('id', menuItemId)
        .single()

      if (error) {
        throw error
      }

      const itemData = data as any
      return {
        id: itemData.id,
        restaurantId: itemData.restaurant_id,
        nameAr: itemData.name_ar,
        nameFr: itemData.name_fr,
        descriptionAr: itemData.description_ar || null,
        descriptionFr: itemData.description_fr || null,
        price: itemData.price,
        imageUrl: itemData.image_url || null,
        imagePath: itemData.image_path || null,
        isAvailable: itemData.is_available ?? true,
        optionsAreRequired: itemData.options_are_required ?? false,
        createdAt: itemData.created_at || '',
        updatedAt: itemData.updated_at || '',
        ingredients: (itemData.menu_item_ingredients || []).map((mi: any) => ({
          id: mi.id,
          ingredientId: mi.ingredient_id,
          quantity: mi.quantity,
          unit: mi.unit,
          menuItemOptionId: mi.menu_item_option_id || null,
          ingredient: {
            id: mi.ingredients.id,
            restaurantId: mi.ingredients.restaurant_id,
            nameAr: mi.ingredients.name_ar,
            nameFr: mi.ingredients.name_fr,
            emoji: mi.ingredients.emoji || '🍽️',
            createdAt: mi.ingredients.created_at || '',
            updatedAt: mi.ingredients.updated_at || '',
          },
        })),
      } as MenuItemWithIngredients
    },
    enabled: !!menuItemId,
  })
}

/**
 * Fetch ALL menu items with their ingredients for a restaurant
 * Used for the ingredients graph visualization
 */
export function useAllMenuItemsWithIngredients(restaurantId: string | null) {
  return useQuery({
    queryKey: [...queryKeys.menuItems.all(), 'all-with-ingredients', restaurantId],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<MenuItemWithIngredients[]> => {
      if (!restaurantId) return []

      if (isAssistantLoggedIn()) {
        // For assistants: use RPC for menu items, then fetch ingredients separately
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_restaurant_menu_items', { p_restaurant_id: restaurantId })

        if (rpcError) throw rpcError

        const menuItemIds = (rpcData || []).map((item: any) => item.id)
        if (menuItemIds.length === 0) return []

        const { data: ingredientsData, error: ingError } = await supabase
          .from('menu_item_ingredients')
          .select(`
            id,
            menu_item_id,
            ingredient_id,
            quantity,
            unit,
            menu_item_option_id,
            ingredients (
              id,
              restaurant_id,
              name_ar,
              name_fr,
              emoji
            )
          `)
          .in('menu_item_id', menuItemIds)

        if (ingError) throw ingError

        // Group ingredients by menu_item_id
        const ingredientsByItem = new Map<string, any[]>()
        for (const ing of ingredientsData || []) {
          const list = ingredientsByItem.get(ing.menu_item_id) || []
          list.push(ing)
          ingredientsByItem.set(ing.menu_item_id, list)
        }

        return (rpcData || [])
          .filter((item: any) => ingredientsByItem.has(item.id) && (ingredientsByItem.get(item.id)?.length ?? 0) > 0)
          .map((item: any) => ({
            id: item.id,
            restaurantId: item.restaurant_id,
            nameAr: item.name_ar,
            nameFr: item.name_fr,
            descriptionAr: item.description_ar || null,
            descriptionFr: item.description_fr || null,
            price: item.price,
            imageUrl: item.image_url || null,
            imagePath: item.image_path || null,
            isAvailable: item.is_available ?? true,
            categoryId: item.category_id || null,
            optionsAreRequired: item.options_are_required ?? false,
            createdAt: item.created_at || '',
            updatedAt: item.updated_at || '',
            ingredients: (ingredientsByItem.get(item.id) || []).map((mi: any) => ({
              id: mi.id,
              ingredientId: mi.ingredient_id,
              quantity: mi.quantity,
              unit: mi.unit,
              menuItemOptionId: mi.menu_item_option_id || null,
              ingredient: {
                id: mi.ingredients.id,
                restaurantId: mi.ingredients.restaurant_id,
                nameAr: mi.ingredients.name_ar,
                nameFr: mi.ingredients.name_fr,
                emoji: mi.ingredients.emoji || '🍽️',
                createdAt: '',
                updatedAt: '',
              },
            })),
          }))
      }

      // Admin user - direct query with join
      const { data, error } = await supabase
        .from('menu_items')
        .select(`
          *,
          menu_item_ingredients (
            id,
            ingredient_id,
            quantity,
            unit,
            menu_item_option_id,
            ingredients (
              id,
              restaurant_id,
              name_ar,
              name_fr,
              emoji
            )
          )
        `)
        .eq('restaurant_id', restaurantId)

      if (error) throw error

      return (data || [])
        .filter((item: any) => item.menu_item_ingredients && item.menu_item_ingredients.length > 0)
        .map((item: any) => ({
          id: item.id,
          restaurantId: item.restaurant_id,
          nameAr: item.name_ar,
          nameFr: item.name_fr,
          descriptionAr: item.description_ar || null,
          descriptionFr: item.description_fr || null,
          price: item.price,
          imageUrl: item.image_url || null,
          imagePath: item.image_path || null,
          isAvailable: item.is_available ?? true,
          categoryId: item.category_id || null,
          optionsAreRequired: item.options_are_required ?? false,
          createdAt: item.created_at || '',
          updatedAt: item.updated_at || '',
          ingredients: (item.menu_item_ingredients || []).map((mi: any) => ({
            id: mi.id,
            ingredientId: mi.ingredient_id,
            quantity: mi.quantity,
            unit: mi.unit,
            menuItemOptionId: mi.menu_item_option_id || null,
            ingredient: {
              id: mi.ingredients.id,
              restaurantId: mi.ingredients.restaurant_id,
              nameAr: mi.ingredients.name_ar,
              nameFr: mi.ingredients.name_fr,
              emoji: mi.ingredients.emoji || '🍽️',
              createdAt: '',
              updatedAt: '',
            },
          })),
        }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Create new menu item with ingredients mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useCreateMenuItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      restaurantId: string
      nameAr?: string
      nameFr?: string
      descriptionAr?: string
      descriptionFr?: string
      price: number
      imageUrl?: string | null
      imagePath?: string | null
      isAvailable?: boolean
      categoryId?: string | null
      optionsAreRequired?: boolean
      ingredients: MenuItemIngredientInput[]
    }) => {
      // Apply fallback: if one language is missing, use the other
      const withFallback = applyLanguageFallback({
        nameAr: request.nameAr || null,
        nameFr: request.nameFr || null,
        descriptionAr: request.descriptionAr || null,
        descriptionFr: request.descriptionFr || null,
      })

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // Create menu item via RPC
        const createParams: Record<string, any> = {
          p_restaurant_id: request.restaurantId,
          p_category_id: request.categoryId || null,
          p_name_ar: withFallback.nameAr || '',
          p_name_fr: withFallback.nameFr || '',
          p_description_ar: withFallback.descriptionAr || null,
          p_description_fr: withFallback.descriptionFr || null,
          p_price: request.price,
          p_image_url: request.imageUrl || null,
          p_is_available: request.isAvailable ?? true,
        };
        const { data: menuItemId, error: menuError } = await supabase.rpc('assistant_create_menu_item', createParams as any)

        if (menuError) {
          if (menuError.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw menuError
        }

        // Add ingredients if provided
        // Note: For now, assistants use direct insert as no specific RPC exists for menu item ingredients
        if (request.ingredients && request.ingredients.length > 0) {
          const ingredientsToInsert = request.ingredients.map((ing) => ({
            menu_item_id: menuItemId,
            ingredient_id: ing.ingredientId,
            quantity: ing.quantity,
            unit: ing.unit,
          }))

          const { error: ingredientsError } = await supabase
            .from('menu_item_ingredients')
            .insert(ingredientsToInsert)

          if (ingredientsError) {
            throw ingredientsError
          }
        }

        return menuItemId
      }

      // Admin user - use direct query
      // Create menu item
      const { data: menuItem, error: menuError } = await supabase
        .from('menu_items')
        .insert({
          restaurant_id: request.restaurantId,
          name_ar: withFallback.nameAr,
          name_fr: withFallback.nameFr,
          description_ar: withFallback.descriptionAr || null,
          description_fr: withFallback.descriptionFr || null,
          price: request.price,
          image_url: request.imageUrl || null,
          image_path: request.imagePath || null,
          is_available: request.isAvailable ?? true,
          category_id: request.categoryId || null,
          options_are_required: request.optionsAreRequired ?? false,
        }as any)
        .select()
        .single()

      if (menuError) {
        throw menuError
      }

      // Add ingredients if provided
      if (request.ingredients && request.ingredients.length > 0) {
        console.log('Saving ingredients:', request.ingredients)
        const ingredientsToInsert = request.ingredients.map((ing) => ({
          menu_item_id: menuItem.id,
          ingredient_id: ing.ingredientId,
          quantity: ing.quantity,
          unit: ing.unit,
          menu_item_option_id: ing.menuItemOptionId || null,
        }))
        console.log('Ingredients to insert:', ingredientsToInsert)

        const { error: ingredientsError } = await supabase
          .from('menu_item_ingredients')
          .insert(ingredientsToInsert)

        if (ingredientsError) {
          console.error('Error inserting ingredients:', ingredientsError)
          throw ingredientsError
        }
        console.log('Ingredients saved successfully')
      } else {
        console.log('No ingredients to save')
      }

      return menuItem.id
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItems.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.menuItems.list({ restaurantId: variables.restaurantId }),
      })
    },
  })
}

/**
 * Update menu item with ingredients mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useUpdateMenuItem(menuItemId: string, restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      nameAr?: string
      nameFr?: string
      descriptionAr?: string
      descriptionFr?: string
      price?: number
      imageUrl?: string | null
      imagePath?: string | null
      oldImagePath?: string | null
      isAvailable?: boolean
      categoryId?: string | null
      optionsAreRequired?: boolean
      ingredients?: MenuItemIngredientInput[]
    }) => {
      // Delete old image if new one is provided
      if (request.oldImagePath && request.imagePath && request.oldImagePath !== request.imagePath) {
        await supabase.storage.from('menu-items').remove([request.oldImagePath])
      }

      // Apply fallback: if one language is missing, use the other
      const withFallback = applyLanguageFallback({
        nameAr: request.nameAr !== undefined ? request.nameAr || null : undefined,
        nameFr: request.nameFr !== undefined ? request.nameFr || null : undefined,
        descriptionAr: request.descriptionAr !== undefined ? request.descriptionAr || null : undefined,
        descriptionFr: request.descriptionFr !== undefined ? request.descriptionFr || null : undefined,
      } as any)

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // Update menu item via RPC
        const updateParams: Record<string, any> = {
          p_restaurant_id: restaurantId,
          p_menu_item_id: menuItemId,
          p_name_ar: withFallback.nameAr || '',
          p_name_fr: withFallback.nameFr || '',
          p_price: request.price ?? 0,
        };
        if (request.categoryId !== undefined) updateParams['p_category_id'] = request.categoryId || null;
        if (withFallback.descriptionAr !== undefined) updateParams['p_description_ar'] = withFallback.descriptionAr;
        if (withFallback.descriptionFr !== undefined) updateParams['p_description_fr'] = withFallback.descriptionFr;
        if (request.imageUrl !== undefined) updateParams['p_image_url'] = request.imageUrl;
        if (request.isAvailable !== undefined) updateParams['p_is_available'] = request.isAvailable;
        const { error: updateError } = await supabase.rpc('assistant_update_menu_item', updateParams as any)

        if (updateError) {
          if (updateError.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw updateError
        }

        // Update ingredients if provided
        // Note: For now, assistants use direct table operations as no specific RPC exists
        if (request.ingredients) {
          // Delete existing ingredients
          await supabase
            .from('menu_item_ingredients')
            .delete()
            .eq('menu_item_id', menuItemId)

          // Add new ingredients
          if (request.ingredients.length > 0) {
            const ingredientsToInsert = request.ingredients.map((ing) => ({
              menu_item_id: menuItemId,
              ingredient_id: ing.ingredientId,
              quantity: ing.quantity,
              unit: ing.unit,
            }))

            const { error: ingredientsError } = await supabase
              .from('menu_item_ingredients')
              .insert(ingredientsToInsert)

            if (ingredientsError) {
              throw ingredientsError
            }
          }
        }

        return { id: menuItemId }
      }

      // Admin user - use direct query
      // Update menu item
      const updateData: {
        name_ar?: string
        name_fr?: string
        description_ar?: string | null
        description_fr?: string | null
        price?: number
        image_url?: string | null
        image_path?: string | null
        is_available?: boolean | null
        category_id?: string | null
        options_are_required?: boolean
      } = {}

      if (withFallback.nameAr !== undefined) updateData.name_ar = withFallback.nameAr || ''
      if (withFallback.nameFr !== undefined) updateData.name_fr = withFallback.nameFr || ''
      if (withFallback.descriptionAr !== undefined) updateData.description_ar = withFallback.descriptionAr || null
      if (withFallback.descriptionFr !== undefined) updateData.description_fr = withFallback.descriptionFr || null
      if (request.price !== undefined) updateData.price = request.price
      if (request.imageUrl !== undefined) updateData.image_url = request.imageUrl
      if (request.imagePath !== undefined) updateData.image_path = request.imagePath
      if (request.isAvailable !== undefined) updateData.is_available = request.isAvailable
      if (request.categoryId !== undefined) updateData.category_id = request.categoryId || null
      if (request.optionsAreRequired !== undefined) updateData.options_are_required = request.optionsAreRequired

      const { data, error: updateError } = await supabase
        .from('menu_items')
        .update(updateData)
        .eq('id', menuItemId)
        .select()
        .single()

      if (updateError) {
        throw updateError
      }

      // Update ingredients if provided
      if (request.ingredients) {
        // Delete existing ingredients
        await supabase
          .from('menu_item_ingredients')
          .delete()
          .eq('menu_item_id', menuItemId)

        // Add new ingredients
        if (request.ingredients.length > 0) {
          const { error: ingredientsError } = await supabase
            .from('menu_item_ingredients')
            .insert(
              request.ingredients.map((ing) => ({
                menu_item_id: menuItemId,
                ingredient_id: ing.ingredientId,
                quantity: ing.quantity,
                unit: ing.unit,
                menu_item_option_id: ing.menuItemOptionId || null,
              }))
            )

          if (ingredientsError) {
            throw ingredientsError
          }
        }
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItems.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.menuItems.list({ restaurantId }),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.menuItems.withIngredients(menuItemId),
      })
    },
  })
}

/**
 * Delete menu item mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useDeleteMenuItem(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (menuItem: { id: string; imagePath?: string | null }) => {
      // Delete image from storage if exists
      if (menuItem.imagePath) {
        await supabase.storage.from('menu-items').remove([menuItem.imagePath])
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { error } = await supabase.rpc('assistant_delete_menu_item', {
          p_restaurant_id: restaurantId,
          p_menu_item_id: menuItem.id,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          // Check if it's a foreign key constraint error
          if (error.code === '23503') {
            throw new Error('MENU_ITEM_IN_USE')
          }
          throw error
        }
        return
      }

      // Admin user - use direct query
      // Delete menu item
      const { error } = await supabase
        .from('menu_items')
        .delete()
        .eq('id', menuItem.id)

      if (error) {
        // Check if it's a foreign key constraint error
        if (error.code === '23503') {
          throw new Error('MENU_ITEM_IN_USE')
        }
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItems.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.menuItems.list({ restaurantId }),
      })
    },
  })
}

/**
 * Toggle menu item availability
 * Supports both admin users and assistants with appropriate permissions
 */
export function useToggleMenuItemAvailability(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ menuItemId, isAvailable }: { menuItemId: string; isAvailable: boolean }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { error } = await supabase.rpc('assistant_toggle_menu_item_availability', {
          p_restaurant_id: restaurantId,
          p_menu_item_id: menuItemId,
          p_is_available: isAvailable,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }
        return
      }

      // Admin user - use direct query
      const { error } = await supabase
        .from('menu_items')
        .update({ is_available: isAvailable })
        .eq('id', menuItemId)

      if (error) {
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItems.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.menuItems.list({ restaurantId }),
      })
    },
  })
}
